import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, DollarSign, Wrench, Gauge, Users, ArrowRight } from "lucide-react";
import type { MaintenanceRecommendation } from "@/lib/maintenance-analytics";

interface MaintenanceRecommendationsProps {
  recommendations: MaintenanceRecommendation[];
}

const categoryConfig = {
  cost: {
    icon: DollarSign,
    label: 'Custo',
    color: 'bg-chart-3'
  },
  provider: {
    icon: Users,
    label: 'Fornecedor',
    color: 'bg-chart-2'
  },
  efficiency: {
    icon: Gauge,
    label: 'Eficiência',
    color: 'bg-primary'
  },
  safety: {
    icon: Wrench,
    label: 'Segurança',
    color: 'bg-destructive'
  }
};

const priorityConfig = {
  high: {
    icon: '🔴',
    label: 'ALTA',
    variant: 'destructive' as const,
    borderColor: 'border-destructive'
  },
  medium: {
    icon: '🟡',
    label: 'MÉDIA',
    variant: 'default' as const,
    borderColor: 'border-warning'
  },
  low: {
    icon: '🟢',
    label: 'BAIXA',
    variant: 'secondary' as const,
    borderColor: 'border-border'
  }
};

export function MaintenanceRecommendations({ recommendations }: MaintenanceRecommendationsProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Sort by priority
  const sortedRecommendations = [...recommendations].sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });

  // Group by priority
  const highPriority = sortedRecommendations.filter(r => r.priority === 'high');
  const mediumPriority = sortedRecommendations.filter(r => r.priority === 'medium');
  const lowPriority = sortedRecommendations.filter(r => r.priority === 'low');

  const renderRecommendationCard = (rec: MaintenanceRecommendation, index: number) => {
    const category = categoryConfig[rec.category];
    const priority = priorityConfig[rec.priority];
    const CategoryIcon = category.icon;

    return (
      <Card key={index} className={`border-l-4 ${priority.borderColor}`}>
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className={`rounded-full p-3 ${category.color} text-white flex-shrink-0`}>
              <CategoryIcon className="h-5 w-5" />
            </div>
            
            <div className="flex-1 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-xl">{priority.icon}</span>
                    <Badge variant={priority.variant}>
                      {priority.label}
                    </Badge>
                    <Badge variant="outline">
                      {category.label}
                    </Badge>
                  </div>
                  <h4 className="text-lg font-semibold mb-2">{rec.title}</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    {rec.description}
                  </p>
                </div>
              </div>

              {rec.potentialSavings !== undefined && rec.potentialSavings > 0 && (
                <div className="flex items-center gap-2 p-3 bg-success/10 rounded-lg border border-success/20">
                  <DollarSign className="h-5 w-5 text-success flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-success">
                      Economia Potencial
                    </p>
                    <p className="text-lg font-bold text-success">
                      {formatCurrency(rec.potentialSavings)}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button size="sm">
                  {rec.action}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Recomendações e Ações Prioritárias</CardTitle>
          <CardDescription>
            {recommendations.length} recomendações baseadas em análise de dados
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recommendations.length > 0 ? (
            <div className="space-y-6">
              {/* High Priority */}
              {highPriority.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-destructive" />
                    <h3 className="text-lg font-semibold">Ações Urgentes</h3>
                  </div>
                  <div className="space-y-4">
                    {highPriority.map((rec, idx) => renderRecommendationCard(rec, idx))}
                  </div>
                </div>
              )}

              {/* Medium Priority */}
              {mediumPriority.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Ações Recomendadas</h3>
                  <div className="space-y-4">
                    {mediumPriority.map((rec, idx) => renderRecommendationCard(rec, idx))}
                  </div>
                </div>
              )}

              {/* Low Priority */}
              {lowPriority.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Melhorias Sugeridas</h3>
                  <div className="space-y-4">
                    {lowPriority.map((rec, idx) => renderRecommendationCard(rec, idx))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="mb-4">
                <Wrench className="h-16 w-16 mx-auto text-success" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                Tudo em ordem! 🎉
              </h3>
              <p className="text-muted-foreground">
                As manutenções estão sendo executadas de forma eficiente.
                Continue com a gestão preventiva regular.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
